// src/pages/GetInvolvedPage.js - ADD API WITHOUT CHANGING UI
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import api from '../services/api';
import '../styles/pages/GetInvolvedPage.css';

const GetInvolvedPage = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.getSettings('flat');
      if (response.success) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);

      const response = await api.submitForm(activeTab, data);

      if (response.success) {
        toast.success('Message sent successfully! Check your email for confirmation.');
        reset();
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail) {
      toast.error('Please enter your email');
      return;
    }
    if (!/^\S+@\S+$/i.test(subscribeEmail)) {
      toast.error('Invalid email address');
      return;
    }

    try {
      const response = await api.subscribeNewsletter(subscribeEmail);
      if (response.success) {
        toast.success('Successfully subscribed!');
        setSubscribeEmail('');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  const formConfigs = {
    general: {
      title: 'General Enquiries',
      fields: [
        { name: 'fullName', type: 'text', placeholder: '* Full name', required: true },
        { name: 'email', type: 'email', placeholder: '* Email Address', required: true },
        { name: 'company', type: 'text', placeholder: '* Company/Institution', required: true },
        { name: 'jobTitle', type: 'text', placeholder: '* Job Title', required: true },
        { name: 'mobile', type: 'tel', placeholder: '* Mobile Number', required: true },
        { name: 'description', type: 'textarea', placeholder: '* Brief description...', required: true }
      ]
    },
    project: {
      title: 'Discuss a Project',
      fields: [
        { name: 'fullName', type: 'text', placeholder: '* Full name', required: true },
        { name: 'email', type: 'email', placeholder: '* Email Address', required: true },
        { name: 'organization', type: 'text', placeholder: '* Organization', required: true },
        { name: 'projectType', type: 'text', placeholder: '* Project Type', required: true },
        { name: 'budget', type: 'text', placeholder: 'Estimated Budget (Optional)', required: false },
        { name: 'timeline', type: 'text', placeholder: '* Expected Timeline', required: true },
        { name: 'projectDetails', type: 'textarea', placeholder: '* Project details and objectives...', required: true }
      ]
    },
    press: {
      title: 'Press Enquiries',
      fields: [
        { name: 'fullName', type: 'text', placeholder: '* Full name', required: true },
        { name: 'email', type: 'email', placeholder: '* Email Address', required: true },
        { name: 'organization', type: 'text', placeholder: '* Media Organization', required: true },
        { name: 'position', type: 'text', placeholder: '* Position/Role', required: true },
        { name: 'deadline', type: 'date', placeholder: '* Response Deadline', required: true },
        { name: 'inquiry', type: 'textarea', placeholder: '* Press inquiry details...', required: true }
      ]
    },
    join: {
      title: 'Join Our Team',
      fields: [
        { name: 'fullName', type: 'text', placeholder: '* Full name', required: true },
        { name: 'email', type: 'email', placeholder: '* Email Address', required: true },
        { name: 'phone', type: 'tel', placeholder: '* Phone Number', required: true },
        { name: 'position', type: 'text', placeholder: '* Position Applied For', required: true },
        { name: 'experience', type: 'text', placeholder: '* Years of Experience', required: true },
        { name: 'portfolio', type: 'url', placeholder: 'Portfolio/LinkedIn URL (Optional)', required: false },
        { name: 'coverLetter', type: 'textarea', placeholder: '* Why do you want to join Supernova Lab?...', required: true }
      ]
    }
  };

  const currentForm = formConfigs[activeTab];

  return (
    <PageWrapper>
      <SEO
        title="Partner With Us | Supernova Lab"
        description="Become a Lab Fellow, propose a pilot project, host a masterclass, or fund a flagship experiment. Let's test tomorrow, together."
        keywords="partner, collaborate, get involved, contact, Barbados"
      />

      <main className="get-involved-page">
        <section className="partner-hero-section">
          <div className="container">
            <h1 className="partner-title">Partner With Us</h1>
            <p className="partner-subtitle">
              Become a Lab Fellow, propose a pilot project, host a masterclass, or<br />
              fund a flagship experiment. Let's test tomorrow, together.
            </p>
            <div className="partner-tabs">
              <button className={`partner-tab ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>General Enquiries</button>
              <button className={`partner-tab ${activeTab === 'project' ? 'active' : ''}`} onClick={() => setActiveTab('project')}>Discuss a Project</button>
              <button className={`partner-tab ${activeTab === 'press' ? 'active' : ''}`} onClick={() => setActiveTab('press')}>Press Enquiries</button>
              <button className={`partner-tab ${activeTab === 'join' ? 'active' : ''}`} onClick={() => setActiveTab('join')}>Join Our Team</button>
            </div>
          </div>
        </section>

        <section className="partner-form-section py-4">
          <div className="container">
            <div className="row">
              <div className="col-lg-6">
                <h2 className="form-section-title mb-4">{currentForm.title}</h2>
                <form onSubmit={handleSubmit(onSubmit)} className="partner-form">
                  {currentForm.fields.map((field, index) => (
                    <div key={index} className="form-group mb-4">
                      {field.type === 'textarea' ? (
                        <>
                          <textarea
                            className="form-control partner-textarea"
                            placeholder={field.placeholder}
                            rows="4"
                            disabled={submitting}
                            {...register(field.name, { required: field.required })}
                          ></textarea>
                          {errors[field.name] && <span className="text-danger small">This field is required</span>}
                        </>
                      ) : (
                        <>
                          <input
                            type={field.type}
                            className="form-control partner-input"
                            placeholder={field.placeholder}
                            disabled={submitting}
                            {...register(field.name, {
                              required: field.required,
                              pattern: field.type === 'email' ? /^\S+@\S+$/i : undefined
                            })}
                          />
                          {errors[field.name] && <span className="text-danger small">
                            {field.type === 'email' ? 'Invalid email' : 'This field is required'}
                          </span>}
                        </>
                      )}
                    </div>
                  ))}
                  <button type="submit" className="btn-send-message" disabled={submitting}>
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="ms-2">
                          <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              </div>
              <div className="col-lg-6">
                <div className="contact-info-box">
                  <h2 className="contact-email">
                    {settings?.contact_email || 'hq@supernovalab.ai'}
                  </h2>
                  <div className="social-links-inline">
                    <a
                      href={settings?.social_instagram || "#instagrm"}
                      className="social-link-inline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-instagram"></i>
                    </a>
                    <a
                      href={settings?.social_linkedin || "#linkedin"}
                      className="social-link-inline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                    <a
                      href={settings?.social_facebook || "#facebook"}
                      className="social-link-inline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </a>
                    <a
                      href={settings?.social_twitter || "#twitter"}
                      className="social-link-inline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-twitter"></i>
                    </a>
                    <a
                      href={settings?.social_youtube || "#youtube"}
                      className="social-link-inline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-youtube"></i>
                    </a>
                    <a
                      href={settings?.social_tiktok || "#youtube"}
                      className="social-link-inline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-tiktok"></i>
                    </a>
                  </div>
                  <p className="contact-location">
                    {settings?.contact_address || 'Bridgetown'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="newsletter-section-alt">
          <div className="container">
            <div className="text-center">
              <h2 className="newsletter-title-alt mb-3">Stay Informed with Supernova</h2>
              <p className="newsletter-subtitle-alt-tool mb-4">Subscribe to our newsletter for the latest updates.</p>
              <form onSubmit={handleSubscribe} className="newsletter-form-alt">
                <div className="input-group-alt">
                  <input type="email" className="form-control newsletter-input-alt" placeholder="Enter your email" value={subscribeEmail} onChange={(e) => setSubscribeEmail(e.target.value)} />
                  <button className="newsletter-btn-alt" type="submit">Subscribe</button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
    </PageWrapper>
  );
};

export default GetInvolvedPage;